import { supabase } from "@/integrations/supabase/client";
import { EntryType } from "@/types";
import { currencyAPI, CurrencyCode, SUPPORTED_CURRENCIES } from "@/api/currency";
import { safeApiCall } from "./index";
import { logActivity, ActivityActions } from "@/utils/activityLogger";
import { utcToDeviceTimezone, getDeviceTimezoneOffset } from "@/utils/dateUtils";
import {
  PaymentMethod,
  AdvanceSubMethod,
  CurrencyMap,
  emptyCurrencyMap,
  addRowToCurrencyMap,
} from "@/lib/paymentMethods";

// Helper function to normalize dates in local timezone
const normalizeDate = (date: Date): string => {
  // Get local date components with timezone adjustment
  const timezoneOffset = getDeviceTimezoneOffset();
  const adjustedDate = new Date(date);
  
  // Get components from the timezone-adjusted date
  const year = adjustedDate.getFullYear();
  const month = adjustedDate.getMonth();
  const day = adjustedDate.getDate();
  
  // Create date at 12:00 local time to avoid timezone issues
  const normalizedDate = new Date(year, month, day, 12, 0, 0);
  
  // Convert to ISO string and take only the date part
  return normalizedDate.toISOString().split('T')[0];
};

// Compute the per-currency official/unofficial converted-amount fields for an
// entry. Extracted so it can be reused for advance draw-down splits.
const computeCurrencyFields = async (
  amount: number,
  originalCurrency: string,
  isOfficial: boolean
): Promise<Record<string, number>> => {
  const fields: Record<string, number> = {};
  if (!amount || amount <= 0) return fields;

  const supportedCurrencies = SUPPORTED_CURRENCIES.map(c => c.value);
  for (const targetCurrency of supportedCurrencies) {
    try {
      const { data: convertedAmount } = await currencyAPI.convertCurrency(
        amount,
        originalCurrency as any,
        targetCurrency as any
      );
      if (convertedAmount !== null) {
        const fieldName = `amount_${targetCurrency.toLowerCase()}_${isOfficial ? 'official' : 'unofficial'}`;
        fields[fieldName] = convertedAmount;
      }
    } catch (convError) {
      // Ignore individual conversion failures (matches prior behaviour)
    }
  }
  return fields;
};

// Currency conversion is linear in the amount, so a split portion can be derived
// by scaling the already-computed fields rather than re-converting.
const scaleCurrencyFields = (
  fields: Record<string, number>,
  factor: number
): Record<string, number> => {
  const scaled: Record<string, number> = {};
  for (const [key, value] of Object.entries(fields)) {
    scaled[key] = value * factor;
  }
  return scaled;
};

// Sum a currency-fields object's TRY official+unofficial (canonical comparison currency).
const tryTotalOfFields = (fields: Record<string, number>): number => {
  return (fields['amount_try_official'] || 0) + (fields['amount_try_unofficial'] || 0);
};

// --- Per-person ledger helpers (advance prepaid credit / open-account payable) ---

// Fetch a person's expense rows, optionally scoped to one project.
const fetchPersonExpenseRows = async (
  personId: string,
  projectId?: string
): Promise<any[]> => {
  let query = supabase
    .from('entries')
    .select('*')
    .eq('person_id', personId)
    .eq('type', 'expense');
  if (projectId) {
    query = query.eq('project_id', projectId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data as any[]) || [];
};

// Net prepaid credit a person holds = Σ advance payments − Σ draw-downs (per currency).
// Advance rows count regardless of cleared status (the money is committed — either
// cost was already increased, or a check is waiting to be paid).
const computeAdvanceCreditMap = (rows: any[]): CurrencyMap => {
  const map = emptyCurrencyMap();
  for (const row of rows) {
    if (row.payment_method === 'advance') {
      addRowToCurrencyMap(map, row, +1);
    } else if (row.counts_toward_cost === false) {
      // Pure draw-down row consuming the advance.
      addRowToCurrencyMap(map, row, -1);
    }
  }
  return map;
};

// Outstanding open-account payable = Σ open_account rows − Σ settlement payments
// (per currency). Settlement rows pay the debt down toward zero.
const computeOpenAccountMap = (rows: any[]): CurrencyMap => {
  const map = emptyCurrencyMap();
  for (const row of rows) {
    if (row.payment_method === 'open_account') {
      addRowToCurrencyMap(map, row, +1); // the debt
    } else if (row.settles_entry_id) {
      addRowToCurrencyMap(map, row, -1); // a payment reducing it
    }
  }
  return map;
};

// Entries API
export const entriesAPI = {
  // Get all entries
  async getEntries() {
    // Use the database function we created to get only the current user's entries
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .order('date', { ascending: false });
    
    if (error) {
      
      throw error;
    }
    
    // Convert dates to device timezone
    return (data || []).map(entry => ({
      ...entry,
      date: entry.date ? utcToDeviceTimezone(entry.date) : entry.date,
      due_date: entry.due_date ? utcToDeviceTimezone(entry.due_date) : entry.due_date
    }));
  },

  // Get entries for a specific project
  async getEntriesByProject(projectId: string) {
    const batchSize = 1000;
    let from = 0;
    let hasMore = true;
    const allEntries: any[] = [];

    // Supabase limits select results by default; fetch in pages to cover all rows.
    while (hasMore) {
      const to = from + batchSize - 1;
      const { data, error } = await supabase
        .from('entries')
        .select('*')
        .eq('project_id', projectId)
        .order('date', { ascending: false })
        .range(from, to);

      if (error) {
        throw error;
      }

      const currentBatch = data || [];
      allEntries.push(...currentBatch);

      if (currentBatch.length < batchSize) {
        hasMore = false;
      } else {
        from += batchSize;
      }
    }

    // Convert dates to device timezone
    return allEntries.map(entry => ({
      ...entry,
      date: entry.date ? utcToDeviceTimezone(entry.date) : entry.date,
      due_date: entry.due_date ? utcToDeviceTimezone(entry.due_date) : entry.due_date
    }));
  },

  // Get entries for a specific person
  async getEntriesByPerson(personId: string) {
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('person_id', personId)
      .order('date', { ascending: false });
    
    if (error) {
      
      throw error;
    }
    
    // Convert dates to device timezone
    return (data || []).map(entry => ({
      ...entry,
      date: entry.date ? utcToDeviceTimezone(entry.date) : entry.date,
      due_date: entry.due_date ? utcToDeviceTimezone(entry.due_date) : entry.due_date
    }));
  },

  // Get a specific entry by ID
  async getEntry(entryId: string) {
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('id', entryId)
      .single();
    
    if (error) {
      
      throw error;
    }
    
    // Convert dates to device timezone
    if (data) {
      return {
        ...data,
        date: data.date ? utcToDeviceTimezone(data.date) : data.date,
        due_date: data.due_date ? utcToDeviceTimezone(data.due_date) : data.due_date
      };
    }
    
    return data;
  },

  // Create a new entry
  async createEntry(entryData: {
    type: EntryType;
    date: Date;
    due_date?: Date;
    project_id: string;
    person_id?: string;
    description: string;
    amount?: number;
    image_url?: string;
    created_by?: string; // We'll get the actual user ID from auth
    currency?: string;
    hasInvoice?: boolean;
    is_official?: boolean; // Added to also accept this field directly
    
    // Multi-currency fields
    // Legacy fields - to be removed
    // amount_try?: number;
    // amount_usd?: number;
    // amount_eur?: number;
    
    // New currency fields with official/unofficial distinction
    amount_try_official?: number;
    amount_try_unofficial?: number;
    amount_usd_official?: number;
    amount_usd_unofficial?: number;
    amount_eur_official?: number;
    amount_eur_unofficial?: number;
    amount_gbp_official?: number;
    amount_gbp_unofficial?: number;
    amount_jpy_official?: number;
    amount_jpy_unofficial?: number;
    amount_rub_official?: number;
    amount_rub_unofficial?: number;
    amount_dinar_official?: number;
    amount_dinar_unofficial?: number;
    amount_sar_official?: number;
    amount_sar_unofficial?: number;
    amount_chf_official?: number;
    amount_chf_unofficial?: number;
    amount_xau_official?: number;
    amount_xau_unofficial?: number;
    amount_btc_official?: number;
    amount_btc_unofficial?: number;
    
    original_currency?: string;
    original_amount?: number;
    usd_rate?: number;
    eur_rate?: number;
    isMiscellaneous?: boolean;

    // Payment-method model (expense entries)
    payment_method?: PaymentMethod;       // cash_card | check | advance | open_account
    advance_sub_method?: AdvanceSubMethod; // for advance / open-account-by-check: cash_card | check
  }) {


    try {
      // Get the authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {

        throw new Error('You must be logged in to create an entry');
      }

      // Block entries on completed projects
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('status')
        .eq('id', entryData.project_id)
        .single();

      if (projectError) throw projectError;
      if (project?.status === 'completed') {
        throw new Error('Cannot add entries to a completed project');
      }

      // Determine the currency and amount - prioritize original_currency if available
      const originalCurrency = entryData.original_currency || entryData.currency || 'TRY';
      const amount = entryData.original_amount || entryData.amount || 0;
      // Important: Use is_official if provided, otherwise fall back to hasInvoice
      const isOfficial = entryData.is_official !== undefined ? entryData.is_official : entryData.hasInvoice === true;
      
      
      
      // 1. Get the latest exchange rates
      const { data: exchangeRates, error: ratesError } = await currencyAPI.getExchangeRates();
      
      if (ratesError) {
        
      }
      
      // 2. Resolve the payment method and validate person linkage
      const paymentMethod = entryData.payment_method;
      const advanceSubMethod = entryData.advance_sub_method;
      const personId = entryData.person_id;

      // Advance (Anlaşma/Bağlantı) and Açık Hesap must be tied to a person.
      if ((paymentMethod === 'advance' || paymentMethod === 'open_account') && !personId) {
        throw new Error('Bu ödeme tipi için bir kişi seçilmelidir.');
      }

      // Common fields shared by every row we may insert (a draw-down can split
      // one expense into two rows).
      const baseRecord: any = {
        type: entryData.type,
        date: normalizeDate(entryData.date),
        due_date: entryData.due_date ? normalizeDate(entryData.due_date) : undefined,
        project_id: entryData.project_id,
        person_id: personId,
        description: entryData.description,
        original_currency: originalCurrency,
        image_url: entryData.image_url || '',
        user_id: user.id,
        is_official: isOfficial,
        is_miscellaneous: entryData.isMiscellaneous,
      };
      if (paymentMethod) baseRecord.payment_method = paymentMethod;
      if (advanceSubMethod) baseRecord.advance_sub_method = advanceSubMethod;

      // 3. Compute the converted currency fields for the full amount
      const fullFields = await computeCurrencyFields(amount, originalCurrency, isOfficial);

      // 4. Auto-draw: an immediate (cash/card) expense for a person who holds
      // prepaid advance credit consumes that credit instead of adding new cost.
      const isImmediateExpense =
        entryData.type === 'expense' &&
        (paymentMethod === undefined || paymentMethod === null || paymentMethod === 'cash_card');
      const eligibleForDraw =
        isImmediateExpense && !!personId && !entryData.isMiscellaneous && amount > 0;

      let rowsToInsert: any[] = [];

      if (eligibleForDraw) {
        const personRows = await fetchPersonExpenseRows(personId!, entryData.project_id);
        const creditMap = computeAdvanceCreditMap(personRows);
        const remainingTRY = creditMap['TRY'] || 0;
        const expenseTRY = tryTotalOfFields(fullFields);

        if (remainingTRY > 0.005 && expenseTRY > 0) {
          const appliedTRY = Math.min(expenseTRY, remainingTRY);
          const fraction = Math.max(0, Math.min(1, appliedTRY / expenseTRY));

          if (fraction >= 0.9999) {
            // Whole expense covered by the advance -> pure draw-down, no new cost.
            rowsToInsert.push({
              ...baseRecord,
              original_amount: amount,
              ...fullFields,
              payment_method: 'cash_card',
              counts_toward_cost: false,
              advance_applied_amount: amount,
            });
          } else {
            // Partial: draw-down portion (no cost) + overflow portion (normal cost).
            const appliedOriginal = amount * fraction;
            const overflowOriginal = amount - appliedOriginal;
            rowsToInsert.push({
              ...baseRecord,
              original_amount: appliedOriginal,
              ...scaleCurrencyFields(fullFields, fraction),
              payment_method: 'cash_card',
              counts_toward_cost: false,
              advance_applied_amount: appliedOriginal,
            });
            rowsToInsert.push({
              ...baseRecord,
              original_amount: overflowOriginal,
              ...scaleCurrencyFields(fullFields, 1 - fraction),
              payment_method: 'cash_card',
              counts_toward_cost: true,
            });
          }
        }
      }

      // 5. Normal single-row insert when no draw-down applied
      if (rowsToInsert.length === 0) {
        rowsToInsert.push({
          ...baseRecord,
          original_amount: amount,
          ...fullFields,
        });
      }

      // 6. Insert
      const { data, error } = await supabase
        .from('entries')
        .insert(rowsToInsert)
        .select();

      if (error) {
        throw error;
      }

      // Log the entry creation activity
      await logActivity(
        user.id,
        ActivityActions.CREATE_ENTRY,
        {
          entry_id: data[0].id,
          project_id: entryData.project_id,
          type: entryData.type,
          amount: amount,
          currency: originalCurrency,
          payment_method: paymentMethod || 'cash_card',
        }
      );


      return data[0]; // Return the created entry

    } catch (error) {

      throw error;
    }
  },

  // Net advance (prepaid) credit a person holds, per currency. Positive = the
  // person owes us goods/work we have already paid for (Anlaşma/Bağlantı).
  async getPersonAdvanceBalance(personId: string, projectId?: string): Promise<CurrencyMap> {
    const rows = await fetchPersonExpenseRows(personId, projectId);
    return computeAdvanceCreditMap(rows);
  },

  // Outstanding open-account payable for a person, per currency. Positive = we
  // owe the person (Açık Hesap), cost not yet recognized.
  async getPersonOpenAccountBalance(personId: string, projectId?: string): Promise<CurrencyMap> {
    const rows = await fetchPersonExpenseRows(personId, projectId);
    return computeOpenAccountMap(rows);
  },

  // Remaining unpaid amount of a single open-account (Açık Hesap) entry, in its
  // original currency = original_amount − Σ settlement payments against it.
  async getOpenAccountOutstanding(openAccountId: string): Promise<number> {
    const { data: oa, error: oaError } = await supabase
      .from('entries')
      .select('original_amount')
      .eq('id', openAccountId)
      .single();
    if (oaError) throw oaError;

    const { data: settlements, error: sError } = await supabase
      .from('entries')
      .select('original_amount')
      .eq('settles_entry_id', openAccountId);
    if (sError) throw sError;

    const total = parseFloat((oa as any)?.original_amount) || 0;
    const paid = ((settlements as any[]) || []).reduce(
      (sum, r) => sum + (parseFloat(r.original_amount) || 0),
      0
    );
    return Math.max(0, total - paid);
  },

  // Pay (part of) an open-account (Açık Hesap) debt. Creates a settlement payment
  // row (cash/card recognizes cost immediately; check when later marked paid) that
  // links to the open-account entry and reduces its outstanding balance. When the
  // outstanding reaches zero the open-account entry is flagged settled.
  async addOpenAccountPayment(
    openAccountId: string,
    amount: number,
    method: AdvanceSubMethod,
    dueDate?: Date,
    userId?: string
  ) {
    return safeApiCall(
      async () => {
        // Load the debt entry to inherit person/project/currency/official status.
        const { data: oa, error: oaError } = await supabase
          .from('entries')
          .select('*')
          .eq('id', openAccountId)
          .single();
        if (oaError) throw oaError;
        const debt = oa as any;

        if (method === 'check' && !dueDate) {
          throw new Error('Çek ile ödeme için vade tarihi gereklidir.');
        }

        const outstanding = await this.getOpenAccountOutstanding(openAccountId);
        if (outstanding <= 0.005) {
          throw new Error('Bu açık hesap zaten kapatılmış.');
        }
        const payAmount = Math.min(amount, outstanding);
        if (payAmount <= 0) {
          throw new Error('Geçerli bir ödeme tutarı girin.');
        }

        const currency = debt.original_currency || 'TRY';
        const isOfficial = debt.is_official === true;
        const fields = await computeCurrencyFields(payAmount, currency, isOfficial);

        const settlementRow: any = {
          type: 'expense',
          date: normalizeDate(new Date()),
          due_date: method === 'check' && dueDate ? normalizeDate(dueDate) : undefined,
          project_id: debt.project_id,
          person_id: debt.person_id,
          description: `Açık hesap ödemesi: ${debt.description || ''}`.trim(),
          original_amount: payAmount,
          original_currency: currency,
          image_url: '',
          user_id: userId || debt.user_id,
          is_official: isOfficial,
          payment_method: method,
          counts_toward_cost: true,
          settles_entry_id: openAccountId,
          ...fields,
        };

        const { data: inserted, error: insError } = await supabase
          .from('entries')
          .insert([settlementRow])
          .select();
        if (insError) throw insError;

        // Mark the debt settled once it is fully covered.
        const remaining = await this.getOpenAccountOutstanding(openAccountId);
        if (remaining <= 0.005) {
          await supabase
            .from('entries')
            .update({
              is_settled: true,
              settled_method: method,
              settled_at: new Date().toISOString(),
              settled_by: userId,
            })
            .eq('id', openAccountId);
        }

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('project-data-refresh'));
          window.dispatchEvent(new CustomEvent('people-data-refresh'));
        }

        return inserted[0];
      },
      {
        timeoutMs: 10000,
        name: `addOpenAccountPayment_${openAccountId}`,
        critical: true,
      }
    );
  },

  // Project-wide advance and open-account ledger totals, per currency.
  async getProjectLedgerTotals(
    projectId: string
  ): Promise<{ advance: CurrencyMap; openAccount: CurrencyMap }> {
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('project_id', projectId)
      .eq('type', 'expense');
    if (error) throw error;
    const rows = (data as any[]) || [];
    return {
      advance: computeAdvanceCreditMap(rows),
      openAccount: computeOpenAccountMap(rows),
    };
  },

  // Update an entry
  async updateEntry(
    entryId: string,
    entryData: {
      type?: EntryType;
      date?: Date;
      due_date?: Date;
      project_id?: string;
      person_id?: string;
      description?: string;
      amount?: number;
      image_url?: string;
      currency?: string;
      original_currency?: string;
      original_amount?: number;
      hasInvoice?: boolean;
      is_official?: boolean; // Added to also accept this field directly
    }
  ) {
    try {
      
      
      // First get the original entry to compare values
      const { data: originalEntry, error: getError } = await supabase
        .from('entries')
        .select('*')
        .eq('id', entryId)
        .single();
      
      if (getError) {
        
        throw getError;
      }
      
      // Determine if amount, currency or official status has changed
      // Check both original_amount and amount fields
      const amountChanged = (entryData.original_amount !== undefined && entryData.original_amount !== (originalEntry as any).original_amount) ||
                            (entryData.amount !== undefined && entryData.amount !== (originalEntry as any).original_amount);
      
      // Check both original_currency and currency fields
      const currencyChanged = (entryData.original_currency !== undefined && entryData.original_currency !== (originalEntry as any).original_currency) ||
                              (entryData.currency !== undefined && entryData.currency !== (originalEntry as any).original_currency);
      
      // Check both is_official and hasInvoice fields
      const officialStatusChanged = 
        (entryData.is_official !== undefined && entryData.is_official !== (originalEntry as any).is_official) ||
        (entryData.hasInvoice !== undefined && entryData.hasInvoice !== (originalEntry as any).is_official);
      
      const projectIdChanged = entryData.project_id !== undefined && entryData.project_id !== (originalEntry as any).project_id;
      
      // If any key values have changed, we need to recalculate currency conversions
      const needsRecalculation = amountChanged || currencyChanged || officialStatusChanged;
      
      
      
      // Convert Date objects to ISO strings for the database
      const formattedData: any = {
        type: entryData.type,
        date: entryData.date ? normalizeDate(entryData.date) : undefined,
        due_date: entryData.due_date ? normalizeDate(entryData.due_date) : undefined,
        project_id: entryData.project_id || (originalEntry as any).project_id,
        person_id: entryData.person_id,
        description: entryData.description,
        image_url: entryData.image_url,
        // Use is_official if provided, otherwise fall back to hasInvoice
        is_official: entryData.is_official !== undefined 
          ? entryData.is_official 
          : entryData.hasInvoice !== undefined 
            ? entryData.hasInvoice 
            : (originalEntry as any).is_official
      };
      
      // If we need to recalculate values
      if (needsRecalculation) {
        // Prioritize new values over existing ones
        const currency = entryData.original_currency || entryData.currency || (originalEntry as any).original_currency || 'TRY';
        const amount = entryData.original_amount || entryData.amount || (originalEntry as any).original_amount || 0;
        // Use is_official from formattedData which already handles the correct priority
        const isOfficial = formattedData.is_official;
        
        
        
        // Update original values
        formattedData.original_amount = amount;
        formattedData.original_currency = currency;
        
        // Get latest exchange rates for conversions
        const { data: exchangeRates, error: ratesError } = await currencyAPI.getExchangeRates();
        
        if (ratesError) {
          
        }
        
        // Get the list of supported currencies
        const supportedCurrencies = SUPPORTED_CURRENCIES.map(c => c.value);
        
        // First, clear all existing amounts
        supportedCurrencies.forEach(curr => {
          formattedData[`amount_${curr.toLowerCase()}_official`] = null;
          formattedData[`amount_${curr.toLowerCase()}_unofficial`] = null;
        });
        
        // Then calculate conversions if amount > 0
        if (amount > 0) {
          
          
          // Process each target currency
          for (const targetCurrency of supportedCurrencies) {
            try {
              // Convert amount from original currency to target currency
              
              const { data: convertedAmount } = await currencyAPI.convertCurrency(
                amount, 
                currency as any, 
                targetCurrency as any
              );
              
              if (convertedAmount !== null) {
                // Set the converted amount in the appropriate field based on isOfficial flag
                const fieldName = `amount_${targetCurrency.toLowerCase()}_${isOfficial ? 'official' : 'unofficial'}`;
                formattedData[fieldName] = convertedAmount;
                
              } else {
                
              }
            } catch (convError) {
              
            }
          }
        } else {
          
        }
      }
      
      
      
      // Update project costs if necessary
      if (needsRecalculation || projectIdChanged) {
        try {
          // If this is a cost-related entry and values have changed, we may need to adjust project costs
          // For simplicity, we're just triggering a recalculation by updating the project's updated_at field
          
          // Get affected project IDs (both old and new if project changed)
          const projectIds = new Set<string>();
          if (originalEntry.project_id) projectIds.add(originalEntry.project_id);
          if (formattedData.project_id) projectIds.add(formattedData.project_id);
          
          // Update each affected project to trigger recalculation
          for (const projectId of projectIds) {
            const { error: updateError } = await supabase
              .from('projects')
              .update({ updated_at: new Date().toISOString() })
              .eq('id', projectId);
              
            if (updateError) {
              
            }
          }
        } catch (costUpdateError) {
          
          // Continue with entry update even if cost update failed
        }
      }

      // Get current user for logging
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Update the entry
      const { data, error } = await supabase
        .from('entries')
        .update(formattedData)
        .eq('id', entryId)
        .select();

      if (error) {
        
        throw error;
      }
      
      // Log the entry update activity
      await logActivity(
        user.id,
        ActivityActions.UPDATE_ENTRY,
        {
          entry_id: entryId,
          project_id: formattedData.project_id,
          type: entryData.type || originalEntry.type,
          changes: Object.keys(entryData).join(', ')
        }
      );
      
      return data[0];
    } catch (error) {
      
      throw error;
    }
  },

  // Delete an entry
  async deleteEntry(entryId: string) {
    try {
      // Get the entry before deleting it for logging
      const { data: entryToDelete, error: getError } = await supabase
        .from('entries')
        .select('*')
        .eq('id', entryId)
        .single();
        
      if (getError) {
        
        throw getError;
      }
      
      // Get current user for logging
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Delete the entry
      const { error } = await supabase
        .from('entries')
        .delete()
        .eq('id', entryId);
        
      if (error) {
        
        throw error;
      }
      
      // Log the entry deletion activity
      await logActivity(
        user.id,
        ActivityActions.DELETE_ENTRY,
        {
          entry_id: entryId,
          project_id: entryToDelete.project_id,
          type: entryToDelete.type,
          description: entryToDelete.description
        }
      );
      
      return true;
    } catch (error) {
      
      throw error;
    }
  },

  // Update check status
  async updateCheckStatus(
    checkId: string,
    status: 'alindi' | 'ertelendi' | 'alinamadi',
    newDueDate?: Date,
    userId?: string
  ) {
    // Use safer pattern to avoid TypeScript errors
    return safeApiCall(
      async () => {
        // First get the check to determine its type
        const { data: check, error: getError } = await supabase
          .from('entries')
          .select('*')
          .eq('id', checkId)
          .single();
        
        if (getError) {
          
          throw getError;
        }
  
        // Prepare the update data based on status
        const updateData: Record<string, unknown> = {};
        
        if (status === 'alindi') {
          // For collected/paid checks, mark them with a special status
          updateData.status = 'completed';
          updateData.is_processed = true;
          // Record when the check was processed
          updateData.processed_at = new Date().toISOString();
          updateData.processed_by = userId;
        } else if (status === 'ertelendi') {
          // For postponed checks, update the due date
          if (!newDueDate) {
            throw new Error('Ertelenen çekler için yeni bir tarih belirtilmelidir.');
          }
          // Use the normalizeDate helper to handle timezone consistently
          updateData.due_date = normalizeDate(newDueDate);
          updateData.is_postponed = true;
          updateData.postponed_at = new Date().toISOString();
          updateData.postponed_by = userId;
        } else if (status === 'alinamadi') {
          // For uncollected/unpaid checks
          updateData.status = 'failed';
          updateData.is_processed = true;
          updateData.processed_at = new Date().toISOString();
          updateData.processed_by = userId;
          updateData.processing_note = 'Çek tahsil edilemedi/ödenemedi';
        }
        
        // Update the check
        const { data, error } = await supabase
          .from('entries')
          .update(updateData)
          .eq('id', checkId)
          .select();
        
        if (error) {
          
          throw error;
        }
        
        // Create appropriate project records based on check type and status
        await this.createCheckStatusRecord(check, status, newDueDate, userId);
        
        // Trigger a refresh event for any components displaying this data
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('check-status-updated', { 
            detail: { checkId, status }
          }));
        }
        
        return data[0];
      },
      {
        timeoutMs: 10000,
        name: `updateCheckStatus_${checkId}`,
        critical: true
      }
    );
  },

  // Create project record for check status updates
  async createCheckStatusRecord(
    check: any,
    status: 'alindi' | 'ertelendi' | 'alinamadi',
    newDueDate?: Date,
    userId?: string
  ) {
    try {
      // Consolidated checks live as `type='expense'` (payment_method='check',
      // advance-by-check, or open-account settled by check). Their cost is
      // recognized directly by the cost predicate once is_processed flips, and
      // postpone updates the due_date in place via updateCheckStatus. So we must
      // NOT spawn a duplicate shadow expense for them — only legacy given/received
      // check entry-types use the shadow-record mechanism below.
      if (check.type !== 'givenCheck' && check.type !== 'receivedCheck') {
        return;
      }

      const isGivenCheck = check.type === 'givenCheck';
      const now = new Date();
      
      // Determine if this check is official or unofficial
      const isOfficial = check.is_official !== undefined ? check.is_official : check.has_invoice === true;
      const statusSuffix = isOfficial ? 'official' : 'unofficial';
      
      // Create a properly typed base record object without the type field
      const baseRecordData: {
        project_id: string;
        user_id: string;
        date: string;
        description: string;
        original_amount?: number;
        original_currency?: string;
        is_official?: boolean;
        has_invoice?: boolean;
        person_id?: string;
        [key: string]: any; // For dynamic currency fields
      } = {
        project_id: check.project_id,
        user_id: userId || check.user_id,
        date: normalizeDate(now),
        description: '',
        original_amount: check.amount || check.original_amount,
        original_currency: check.currency || check.original_currency || 'TRY',
        is_official: isOfficial,
        has_invoice: isOfficial
      };

      // Will be set to true if we need to create a record
      let shouldCreateRecord = false;
      let recordType: string | undefined;
      let description = '';
      let hasInvoice: boolean | undefined;
      let personId: string | undefined;

      if (isGivenCheck) {
        // Handle given check (çek verilen)
        if (status === 'alindi') {
          // Odendi - Add as an expense to project costs (without entry ID in name)
          recordType = 'expense';
          description = `Paid Check: ${check.description || check.check_number || ''}`;
          hasInvoice = check.has_invoice;
          personId = check.person_id;
          shouldCreateRecord = true;
        } else if (status === 'alinamadi') {
          // Odenemedi - Add record as not paid
          recordType = 'expense';
          description = `Unpaid Check: ${check.description || check.check_number || ''} (${check.id})`;
          hasInvoice = false;
          shouldCreateRecord = true;
        }
      } else {
        // Handle received check (çek alınan)
        if (status === 'alindi') {
          // Tahsil Edildi - Add record for detailed report (without entry ID in name)
          recordType = 'expense';
          description = `Collected Check: ${check.description || check.check_number || ''}`;
          hasInvoice = check.has_invoice;
          shouldCreateRecord = true;
        } else if (status === 'alinamadi') {
          // Tahsil Edilemedi - Add record for detailed report
          recordType = 'expense';
          description = `Uncollected Check: ${check.description || check.check_number || ''} (${check.id})`;
          hasInvoice = false;
          shouldCreateRecord = true;
        }
      }

      // For both types, if status is ertelendi (delayed)
      if (status === 'ertelendi' && newDueDate) {
        // Create a new check entry with the new due date
        const delayedCheckData: any = {
          type: check.type,
          project_id: check.project_id,
          person_id: check.person_id,
          description: `Postponed Check: ${check.description || check.check_number || ''}`,
          date: normalizeDate(now),
          due_date: normalizeDate(newDueDate),
          original_amount: check.amount || check.original_amount,
          original_currency: check.currency || check.original_currency || 'TRY',
          is_official: isOfficial,
          has_invoice: isOfficial,
          check_number: check.check_number,
          image_url: check.image_url,
          user_id: userId || check.user_id,
          is_postponed: true,
          postponed_from: check.id,
          postponed_at: normalizeDate(now),
          postponed_by: userId
        };

        // Copy all currency fields with new naming convention 
        for (const currency of SUPPORTED_CURRENCIES.map(c => c.value.toLowerCase())) {
          const officialFieldName = `amount_${currency}_official`;
          const unofficialFieldName = `amount_${currency}_unofficial`;
          
          if (check[officialFieldName] !== undefined) {
            delayedCheckData[officialFieldName] = check[officialFieldName];
          }
          if (check[unofficialFieldName] !== undefined) {
            delayedCheckData[unofficialFieldName] = check[unofficialFieldName];
          }
        }

        // Insert the delayed check entry
        await supabase
          .from('entries')
          .insert(delayedCheckData);

        // Delete the original check record as requested
        await supabase
          .from('entries')
          .delete()
          .eq('id', check.id);

        // Do NOT create an expense entry for delayed checks
        return; // Exit early, no expense record needed for delayed checks
      }

      // Only insert record if we have determined we should create one
      if (shouldCreateRecord && recordType) {
        // Get the amount and original currency
        const amount = check.amount || check.original_amount || 0;
        const originalCurrency = check.currency || check.original_currency || 'TRY';

        // Get the latest exchange rates for currency conversion
        const { data: exchangeRates, error: ratesError } = await currencyAPI.getExchangeRates();
        
        if (ratesError) {
          
        }

        // Create the final record with the required type field
        const finalRecordData = {
          ...baseRecordData,
          type: recordType,
          description: description,
          is_official: hasInvoice,
          has_invoice: hasInvoice,
          person_id: personId,
          original_amount: amount,
          original_currency: originalCurrency
        };

        // Calculate and set currency conversions if amount > 0
        if (amount > 0) {
          // Get the list of supported currencies
          const supportedCurrencies = SUPPORTED_CURRENCIES.map(c => c.value);
          
          // Process each target currency
          for (const targetCurrency of supportedCurrencies) {
            try {
              // Convert amount from original currency to target currency
              const { data: convertedAmount } = await currencyAPI.convertCurrency(
                amount, 
                originalCurrency as any, 
                targetCurrency as any
              );
              
              if (convertedAmount !== null) {
                // Set the converted amount in the appropriate field based on hasInvoice flag
                const fieldName = `amount_${targetCurrency.toLowerCase()}_${hasInvoice ? 'official' : 'unofficial'}`;
                finalRecordData[fieldName] = convertedAmount;
              }
            } catch (convError) {
              
            }
          }
        }

        const { error } = await supabase
          .from('entries')
          .insert(finalRecordData);

        if (error) {
          
        }
      }
    } catch (error) {
      
    }
  }
};